import React, {
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Button,
  Empty,
  Input,
  List,
  Select,
} from 'antd';

import defaultUserAvatarImage from '../../styles/images/no_testimonial_avatar_image.png';
import i18n from '../../config/i18n';
import { PATIENT_ID } from '../../constants/RoleConstants';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  editName: {
    minHeight: '104px',
  },
  memberList: {
    minHeight: '268px',
    height: 'calc(100% - 152px)',
  },
  buttonWrapper: {
    height: '44px',
  },
  addListWrapper: {
    height: 'calc(100% - 32px)',
  },
  select: {
    width: '100%',
  },
  list: {
    height: '100%',
    overflowY: 'scroll',
  },
  button: {
    width: 'calc(50% - 24px)',
    margin: '0 12px',
  },
  title: {
    fontSize: '15px',
    padding: '12px',
    backgroundColor: '#2D3E4F',
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  content: {
    height: 'calc(100% - 70px)',
    margin: '12px',
    width: 'calc(100% - 24px)',
  },
  user: {
    item: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
    },
    avatar: {
      marginRight: '9px',
      minWidth: '32px',
    },
    name: {
      marginRight: '9px',

      // 50px is the sum of avatar.marginRight, avatar.minWidth and name.marginRight
      maxWidth: 'calc(100% - 50px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    withCloseButton: {
      // 74px is the sum of avatar.marginRight, avatar.minWidth,
      // name.marginRight and the close button width
      maxWidth: 'calc(100% - 74px)',
    },
  },
};

const CreateChannelForm = ({
  invitableUsers,
  onCancel,
  onSubmit,
}) => {
  const [newChannelName, setNewChannelName] = useState(i18n.t('newChannel'));
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isPatientSelected, setIsPatientSelected] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleDeselect = (userId, roleId) => {
    setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    if (roleId === PATIENT_ID) setIsPatientSelected(false);
  };

  const generateSelectedUserItems = (userId) => {
    const {
      avatar,
      firstName,
      lastName,
      roleId,
    } = invitableUsers.find(user => user.id === userId);
    return (
      <List.Item style={styles.user.item}>
        <Button
          icon="close"
          onClick={() => handleDeselect(userId, roleId)}
          size="small"
          style={styles.close}
          type="link"
        />
        <Avatar
          src={defaultUserAvatarImage}
          srcSet={avatar}
          style={styles.user.avatar}
        />
        <div style={{ ...styles.user.name, ...styles.user.withCloseButton }}>
          {firstName} {lastName}
        </div>
      </List.Item>
    );
  };

  const generateSelectOptions = users => (
    users
      .filter(({ id }) => !selectedUserIds.includes(id))
      .map(({
        avatar,
        id,
        firstName,
        lastName,
        roleId,
      }) => {
        const fullName = `${firstName} ${lastName}`;
        return (
          <Select.Option
            disabled={isPatientSelected && roleId === PATIENT_ID}
            key={id}
            label={fullName}
            value={id}
            style={styles.user.item}
            role={roleId}
          >
            <Avatar
              src={defaultUserAvatarImage}
              srcSet={avatar}
              style={styles.user.avatar}
            />
            <div style={styles.user.name}>{fullName}</div>
          </Select.Option>
        );
      })
  );

  // Edit name.
  const handleInput = (e) => {
    setNewChannelName(e.target.value);
  };

  const handleCancel = () => {
    onCancel();
    setSelectedUserIds([]);
    setNewChannelName(i18n.t('newChannel'));
    setIsPatientSelected(false);
  };
  const handleConfirm = () => {
    onSubmit({
      name: newChannelName,
      members: selectedUserIds,
    });
    setSelectedUserIds([]);
    setNewChannelName(i18n.t('newChannel'));
  };
  const handleSelect = (value, option) => {
    setSelectedUserIds([...selectedUserIds, value]);
    if (option.props.role === PATIENT_ID) setIsPatientSelected(true);
  };

  return (
    <div
      style={styles.root}
    >
      <div style={styles.editName}>
        <div style={styles.title}>
          {i18n.t('name')}
        </div>
        <div style={styles.content}>
          <Input
            value={newChannelName}
            onChange={handleInput}
          />
        </div>
      </div>
      <div style={styles.memberList}>
        <div style={styles.title}>
          {selectedUserIds.length} {i18n.t('members')}
        </div>
        <div style={styles.content}>
          <Select
            onSelect={handleSelect}
            onDropdownVisibleChange={open => setIsMenuOpen(open)}
            optionFilterProp="label"
            placeholder={i18n.t(isMenuOpen ? ['channel', 'searchUser'] : 'addMembersHint')}
            showSearch
            style={styles.select}
            value={undefined}
            notFoundContent={
              <Empty
                description={i18n.t(['channel', 'noUser'])}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            }
          >
            {generateSelectOptions(invitableUsers)}
          </Select>
          <div style={styles.addListWrapper}>
            <List
              itemLayout="vertical"
              dataSource={selectedUserIds}
              renderItem={generateSelectedUserItems}
              size="small"
              style={styles.list}
              locale={{
                emptyText: (
                  <Empty
                    description={i18n.t(['channel', 'noUserToAdd'])}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </div>
        </div>
      </div>
      <div style={styles.buttonWrapper}>
        <Button
          onClick={handleCancel}
          style={styles.button}
        >
          {i18n.t('cancel')}
        </Button>
        <Button
          disabled={selectedUserIds.length <= 0}
          onClick={handleConfirm}
          style={styles.button}
          type="primary"
        >
          {i18n.t('confirm')}
        </Button>
      </div>
    </div>
  );
};

CreateChannelForm.propTypes = {
  invitableUsers: PropTypes.arrayOf(PropTypes.object),
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

CreateChannelForm.defaultProps = {
  invitableUsers: [],
};

export default CreateChannelForm;



// WEBPACK FOOTER //
// ./src/components/messaging/CreateChannelForm.js